/**
 * Admin attendance-claims queue (iter 181, Dirk).
 *
 * Inline (no modals) panel on the Attendance Register page. Lists all
 * pending time-amendment claims with one-tap approve / reject. On
 * approve, the backend writes the attendance_event AND an audit row in
 * attendance_event_overrides — fully reflected in the register below.
 *
 * Refreshes every 60s silently (matches the rest of the page).
 */
import { useEffect, useState } from "react";
import { toast } from "../ui/sonner";
import api from "../../lib/api";
import { Check, X, ClipboardCheck, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const KIND_LABELS = {
  in: "Clock in",
  out: "Clock out",
  break_start: "Break start",
  break_end: "Break end",
};

export default function AdminClaimsQueue() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // claim id being expanded for note entry
  const [adminNote, setAdminNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/team/attendance/claims?status=pending&days=30");
      setItems(data?.items || []);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await api.post(`/team/attendance/claims/${id}/decision`, {
        decision,
        admin_note: (noteFor === id ? adminNote : "").trim() || null,
      });
      toast.success(`Claim ${decision === "approve" ? "approved" : "rejected"}.`);
      setNoteFor(null); setAdminNote("");
      load();
      // Tell the rest of the page to refresh the register.
      window.dispatchEvent(new Event("hv:attendance-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally { setBusyId(null); }
  };

  if (items.length === 0) return null;

  return (
    <div className="hv-card p-0 overflow-hidden border border-amber-500/30 bg-amber-500/[0.04]" data-testid="admin-claims-queue">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-amber-500/[0.06]"
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-claims-queue-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <ClipboardCheck className="w-4 h-4 text-amber-300" />
          <span className="font-medium text-amber-100">{items.length} pending amendment{items.length === 1 ? "" : "s"}</span>
          <span className="text-[11px] text-amber-200/70">Tap to review</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-amber-200" /> : <ChevronDown className="w-4 h-4 text-amber-200" />}
      </button>

      {open && (
        <ul className="divide-y divide-white/5 border-t border-amber-500/20">
          {items.map((c) => (
            <li key={c.id} className="px-4 py-3" data-testid={`admin-claim-${c.id}`}>
              <div className="flex flex-wrap items-start gap-2">
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm">
                    <span className="font-medium text-white">{c.user_name || c.user_id}</span>
                    <span className="text-[#a0aabf]"> · </span>
                    <span className="text-amber-200">{KIND_LABELS[c.kind] || c.kind}</span>
                    <span className="text-[#a0aabf]"> at </span>
                    <span className="text-white tabular-nums">{(c.claimed_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-xs text-[#cfd6e4] mt-0.5">{c.reason}</div>
                  {c.note && (
                    <div className="text-[11px] text-[#a0aabf] italic mt-0.5">Note: {c.note}</div>
                  )}
                  <div className="text-[10px] text-[#647087] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>submitted {(c.submitted_at || "").slice(0, 16).replace("T", " ")}</span>
                    <ClaimLag claimedAt={c.claimed_at} submittedAt={c.submitted_at} />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => decide(c.id, "approve")}
                    disabled={busyId === c.id}
                    className="hv-btn text-xs border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50"
                    data-testid={`admin-claim-approve-${c.id}`}
                  >
                    {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (noteFor !== c.id) { setNoteFor(c.id); setAdminNote(""); return; }
                      decide(c.id, "reject");
                    }}
                    disabled={busyId === c.id}
                    className="hv-btn text-xs border border-red-500/40 text-red-200 hover:bg-red-500/10 disabled:opacity-50"
                    data-testid={`admin-claim-reject-${c.id}`}
                  >
                    {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    {noteFor === c.id ? "Confirm reject" : "Reject"}
                  </button>
                </div>
              </div>
              {noteFor === c.id && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Reason for rejection (visible to user)"
                    maxLength={500}
                    className="hv-input text-xs flex-1"
                    data-testid={`admin-claim-reject-note-${c.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => { setNoteFor(null); setAdminNote(""); }}
                    className="text-[11px] text-[#647087] hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * ClaimLag — Iter 370 audit signal. Shows how many days after the
 * event the user actually submitted the amendment request. HR uses
 * this to spot staff getting reckless with not signing in and just
 * requesting corrections after the fact.
 */
function ClaimLag({ claimedAt, submittedAt }) {
  if (!claimedAt || !submittedAt) return null;
  try {
    const c = new Date(claimedAt).getTime();
    const s = new Date(submittedAt).getTime();
    const diffDays = Math.floor((s - c) / 86_400_000);
    if (diffDays <= 0) return <span className="text-emerald-300">· same-day request</span>;
    let color = "text-[#647087]";
    if (diffDays >= 7) color = "text-rose-300 font-semibold";
    else if (diffDays >= 2) color = "text-amber-300";
    return (
      <span className={color} data-testid="admin-claim-lag">
        · requested {diffDays} day{diffDays === 1 ? "" : "s"} after
      </span>
    );
  } catch (_) {
    return null;
  }
}
